/** Extruded item sprites, the entity-fire overlay and the arrow model. */

import * as THREE from 'three';
import type { EnvUniforms } from '../env';
import { entityMaterial, sheetTexture, type EntityMat } from './core';
import { applyThirdPerson, type HeldKind } from '../heldItems';

// --- extruded 16x16 sprite (vanilla item model look), 1 block wide, centred, facing +Z -----------

const itemTex = new Map<string, THREE.Texture>();
const itemGeo = new Map<string, THREE.BufferGeometry>();

function itemTexture(name: string): THREE.Texture {
  let t = itemTex.get(name);
  if (!t) {
    t = new THREE.Texture();
    t.magFilter = THREE.NearestFilter; t.minFilter = THREE.NearestFilter;
    t.generateMipmaps = false; t.flipY = false; t.colorSpace = THREE.SRGBColorSpace;
    itemTex.set(name, t);
  }
  return t;
}

/** Mesh of `mc/item/<name>.png` extruded 1px; geometry fills in once the image loads. */
export function itemSpriteMesh(env: EnvUniforms, name: string, flat = false, srgbOut = false): { mesh: THREE.Mesh; mat: EntityMat } {
  const tex = itemTexture(name);
  const mat = entityMaterial(env, tex, { doubleSided: true, flat, srgbOut });
  const mesh = new THREE.Mesh(new THREE.BufferGeometry(), mat);
  mesh.frustumCulled = false;
  const cached = itemGeo.get(name);
  if (cached) { mesh.geometry = cached; return { mesh, mat }; }
  const img = new Image();
  img.onload = () => {
    tex.image = img; tex.needsUpdate = true;
    const cv = document.createElement('canvas');
    cv.width = img.naturalWidth; cv.height = img.naturalHeight;
    const cx = cv.getContext('2d')!;
    cx.drawImage(img, 0, 0);
    const w = cv.width, h = cv.height;
    const d = cx.getImageData(0, 0, w, h).data;
    const solid = (x: number, y: number): boolean => x >= 0 && y >= 0 && x < w && y < h && d[(y * w + x) * 4 + 3] > 25;
    const pos: number[] = [], nor: number[] = [], uv: number[] = [], idx: number[] = [];
    const u = 1 / w, ht = 0.5 / 16;
    const quad = (p: number[][], n: number[], px: number, py: number): void => {
      const b = pos.length / 3;
      for (const q of p) { pos.push(q[0], q[1], q[2]); nor.push(n[0], n[1], n[2]); uv.push((px + 0.5) * u, (py + 0.5) / h); }
      idx.push(b, b + 1, b + 2, b, b + 2, b + 3);
    };
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      if (!solid(x, y)) continue;
      const x0 = x / w - 0.5, x1 = (x + 1) / w - 0.5, y1 = 0.5 - y / h, y0 = 0.5 - (y + 1) / h;
      quad([[x0, y0, ht], [x1, y0, ht], [x1, y1, ht], [x0, y1, ht]], [0, 0, 1], x, y);
      quad([[x1, y0, -ht], [x0, y0, -ht], [x0, y1, -ht], [x1, y1, -ht]], [0, 0, -1], x, y);
      if (!solid(x - 1, y)) quad([[x0, y0, -ht], [x0, y0, ht], [x0, y1, ht], [x0, y1, -ht]], [-1, 0, 0], x, y);
      if (!solid(x + 1, y)) quad([[x1, y0, ht], [x1, y0, -ht], [x1, y1, -ht], [x1, y1, ht]], [1, 0, 0], x, y);
      if (!solid(x, y - 1)) quad([[x0, y1, ht], [x1, y1, ht], [x1, y1, -ht], [x0, y1, -ht]], [0, 1, 0], x, y);
      if (!solid(x, y + 1)) quad([[x0, y0, -ht], [x1, y0, -ht], [x1, y0, ht], [x0, y0, ht]], [0, -1, 0], x, y);
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    g.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
    g.setIndex(idx);
    itemGeo.set(name, g);
    mesh.geometry = g;
  };
  img.src = `mc/item/${name}.png`;
  return { mesh, mat };
}

/**
 * Pose a sprite mesh in a humanoid's right hand with its vanilla `thirdperson_righthand` display
 * transform (render/heldItems.ts). `kind` true/false is the legacy bow / handheld switch.
 */
export function applyHandheld(o: THREE.Object3D, kind: boolean | HeldKind): void {
  applyThirdPerson(o, kind === true ? 'bow' : kind === false ? 'handheld' : kind);
}

// --- fire overlay -------------------------------------------------------------------------------

/** fire_0 / fire_1 are 16 px wide vertical strips of `FRAMES` 16x16 frames. */
const FRAMES = 32;
const FIRE_TEX = 16;

const FIRE_V = `
varying vec2 vUv; varying vec3 vWp;
void main(){ vUv = uv; vec4 w = modelMatrix * vec4(position,1.0); vWp = w.xyz; gl_Position = projectionMatrix * viewMatrix * w; }`;
const FIRE_F = `
precision highp float;
uniform sampler2D uMap; uniform float uFrame; uniform float uFogNear; uniform float uFogFar; uniform vec3 uFogColor;
varying vec2 vUv; varying vec3 vWp;
void main(){
  // DataTexture rows run top-down (flipY off), and the plane's v=0 is its bottom:
  // 1-vUv.y maps the frame's hot base to the floor and its tips upward.
  vec4 t = texture2D(uMap, vec2(vUv.x, (uFrame + 1.0 - vUv.y) / float(FRAMES)));
  if (t.a < 0.1) discard;
  vec3 c = t.rgb * 1.15;
  c = mix(c, uFogColor, smoothstep(uFogNear, uFogFar, length(cameraPosition - vWp)));
#ifdef OUT_SRGB
  c = pow(c, vec3(1.0/2.2));
#endif
  gl_FragColor = vec4(c, 1.0);
}`;

let fireTex: [THREE.Texture, THREE.Texture] | null = null;

/**
 * `fire_0` / `fire_1` are 16x512 strips of 32 frames. In this asset dump they are
 * plain RGB with the background *blacked out* instead of keyed out, so sampling
 * them directly yields a=1 everywhere — an opaque black square. Decode them to
 * RGBA and key alpha from luminance, exactly like the block-fire sheets in
 * render/fire.ts.
 */
function decodeFireSheet(img: HTMLImageElement, out: Uint8Array): void {
  const cv = document.createElement('canvas');
  cv.width = FIRE_TEX; cv.height = FIRE_TEX * FRAMES;
  const cx = cv.getContext('2d', { willReadFrequently: true });
  if (!cx) return;
  cx.imageSmoothingEnabled = false;
  cx.drawImage(img, 0, 0, cv.width, cv.height);
  const src = cx.getImageData(0, 0, cv.width, cv.height).data;
  let hasAlpha = false;
  for (let i = 3; i < src.length; i += 4) if (src[i] !== 255) { hasAlpha = true; break; }
  for (let i = 0, n = cv.width * cv.height; i < n; i++) {
    const r = src[i * 4], g = src[i * 4 + 1], b = src[i * 4 + 2];
    out[i * 4] = r; out[i * 4 + 1] = g; out[i * 4 + 2] = b;
    out[i * 4 + 3] = hasAlpha ? src[i * 4 + 3] : (Math.max(r, g, b) > 8 ? 255 : 0);
  }
}

function fireTexture(name: string): THREE.Texture {
  const data = new Uint8Array(FIRE_TEX * FRAMES * FIRE_TEX * 4);
  const t = new THREE.DataTexture(data, FIRE_TEX, FIRE_TEX * FRAMES, THREE.RGBAFormat);
  t.colorSpace = THREE.SRGBColorSpace;
  t.magFilter = THREE.NearestFilter;
  t.minFilter = THREE.NearestFilter;
  t.generateMipmaps = false;
  t.flipY = false; // row 0 = the frame's top row, so the shader flips v itself
  const img = new Image();
  img.onload = () => { decodeFireSheet(img, data); t.needsUpdate = true; };
  img.onerror = () => { for (let i = 0; i < data.length; i += 4) { const k = (((i >> 4) ^ (i >> 9)) & 1); data[i] = k ? 0 : 248; data[i + 1] = 0; data[i + 2] = k ? 0 : 248; data[i + 3] = 255; } t.needsUpdate = true; }; // missing-texture marker
  img.src = `mc/${name}.png`;
  return t;
}

function fireTextures(): [THREE.Texture, THREE.Texture] {
  return (fireTex ??= [fireTexture('fire_0'), fireTexture('fire_1')]);
}

/** Four animated flame walls around a w x h hitbox (vanilla renderFlame). Origin at the feet. */
export class FireOverlay {
  readonly group = new THREE.Group();
  private mats: THREE.ShaderMaterial[] = [];
  constructor(env: EnvUniforms, w: number, h: number, srgbOut = false) {
    const tex = fireTextures();
    const s = w * 1.4;
    const geo = new THREE.PlaneGeometry(s, h);
    geo.translate(0, h / 2, 0);
    for (let i = 0; i < 4; i++) {
      const m = new THREE.ShaderMaterial({
        vertexShader: FIRE_V, fragmentShader: FIRE_F, side: THREE.DoubleSide,
        defines: { FRAMES: `${FRAMES}`, ...(srgbOut ? { OUT_SRGB: '' } : {}) },
        uniforms: { uMap: { value: tex[i & 1] }, uFrame: { value: 0 }, uFogNear: env.uFogNear, uFogFar: env.uFogFar, uFogColor: env.uFogColor },
      });
      this.mats.push(m);
      const mesh = new THREE.Mesh(geo, m);
      mesh.frustumCulled = false;
      const g = new THREE.Group();
      g.rotation.y = (i * Math.PI) / 2;
      mesh.position.z = s * 0.4;
      g.add(mesh);
      this.group.add(g);
    }
    this.group.visible = false;
  }
  update(on: boolean, timeSec: number): void {
    this.group.visible = on;
    if (!on) return;
    // one frame per game tick (20 fps), the four walls phase-offset like vanilla
    const f = Math.floor(timeSec * 20) % FRAMES;
    for (let i = 0; i < 4; i++) this.mats[i].uniforms.uFrame.value = (f + i * 8) % FRAMES;
  }
  dispose(): void { for (const m of this.mats) m.dispose(); }
}

// --- arrow --------------------------------------------------------------------------------------

let arrowGeo: THREE.BufferGeometry | null = null;
/** Vanilla arrow: two crossed 16x4 shaft planes + back fin cross, 0.05625 px scale, +X = flight direction. */
export function arrowGeometry(): THREE.BufferGeometry {
  if (arrowGeo) return arrowGeo;
  const pos: number[] = [], nor: number[] = [], uv: number[] = [], idx: number[] = [];
  const S = 0.05625;
  const quad = (v: number[][], n: number[]): void => {
    const b = pos.length / 3;
    for (const q of v) { pos.push(q[0] * S, -q[1] * S, q[2] * S); nor.push(n[0], n[1], n[2]); uv.push(q[3] / 32, q[4] / 32); }
    idx.push(b, b + 1, b + 2, b, b + 2, b + 3);
  };
  // fin plane at x=-7 (+ -4 translate): both sides, rotated 45deg like vanilla's two fin quads
  for (const ang of [Math.PI / 4, -Math.PI / 4]) {
    const c = Math.cos(ang), s = Math.sin(ang);
    const p = (y: number, z: number, u: number, v: number): number[] => [-11, y * c - z * s, y * s + z * c, u, v];
    quad([p(-2, -2, 0, 5), p(-2, 2, 5, 5), p(2, 2, 5, 10), p(2, -2, 0, 10)], [1, 0, 0]);
    quad([p(-2, 2, 0, 5), p(-2, -2, 5, 5), p(2, -2, 5, 10), p(2, 2, 0, 10)], [-1, 0, 0]);
  }
  // shaft planes (two crossed, vertical + horizontal)
  for (let i = 0; i < 2; i++) {
    const rot = (y: number, z: number): [number, number] => (i === 0 ? [y, z] : [-z, y]);
    const mk = (x: number, y: number, u: number, v: number): number[] => { const [yy, zz] = rot(y, 0); return [x - 4, yy, zz, u, v]; };
    quad([mk(-8, -2, 0, 0), mk(8, -2, 16, 0), mk(8, 2, 16, 5), mk(-8, 2, 0, 5)], [0, 1, 0]);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(idx);
  return (arrowGeo = g);
}

export function arrowMesh(env: EnvUniforms): { mesh: THREE.Mesh; mat: EntityMat } {
  const mat = entityMaterial(env, sheetTexture('entity/projectiles/arrow'), { doubleSided: true });
  const mesh = new THREE.Mesh(arrowGeometry(), mat);
  mesh.frustumCulled = false;
  mesh.rotation.order = 'YZX';
  return { mesh, mat };
}
